import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, push, serverTimestamp, update } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';

const PartnerForm = ({ onSuccess, onCancel, partnerData }) => {
  const [formData, setFormData] = useState(partnerData || {
    name: "",
    companyName: "",
    address: "",
    address2: "",
    landmark: "",
    state: "Maharashtra",
    city: "",
    pincode: "",
    country: "India",
    taxNo1: "",
    taxNo2: "",
    mobileNo: "",
    email: "",
    aadharNo: "",
    commissionAmount: "",
    commissionPercentage: ""
  });

  const [currentUser, setCurrentUser] = useState(null);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  // Input validation functions
  const validateName = (value) => /^[a-zA-Z\s]*$/.test(value);
  const validateAadhar = (value) => /^\d{0,12}$/.test(value);
  const validateCity = (value) => /^[a-zA-Z\s]*$/.test(value);
  const validatePincode = (value) => /^\d{0,6}$/.test(value);
  const validateMobile = (value) => /^\d{0,10}$/.test(value);
  const validateTaxNo = (value) => /^[a-zA-Z0-9]*$/.test(value);
  const validateEmail = (value) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);

  useEffect(() => {
    const fetchCountries = async () => {
      setLoading(true);
      try {
        const response = await fetch('https://restcountries.com/v3.1/name/india');
        if (!response.ok) throw new Error('Failed to fetch country data');
        const data = await response.json();
        const indiaCountry = data
          .filter(country => country.name.common === 'India')
          .map(country => ({
            name: country.name.common,
            code: country.cca2
          }));
        setCountries(indiaCountry);
      } catch (error) {
        console.error('Error fetching countries:', error);
        alert('Failed to load country data.');
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      if (!formData.country) return;
      setLoading(true);
      try {
        const response = await fetch('https://countriesnow.space/api/v0.1/countries/states', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ country: formData.country })
        });
        if (!response.ok) throw new Error('Failed to fetch states');
        const data = await response.json();
        if (data.error) throw new Error(data.msg);
        setStates(data.data.states || []);
      } catch (error) {
        console.error('Error fetching states:', error);
        alert('Failed to load states.');
        setStates([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStates();
  }, [formData.country]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validate inputs
    if (name === 'name' || name === 'companyName') {
      if (value && !validateName(value)) {
        alert('Name and Company Name should only contain letters and spaces.');
        return;
      }
    }
    if (name === 'aadharNo') {
      if (value && !validateAadhar(value)) {
        alert('Aadhar Number must contain only digits (up to 12).');
        return;
      }
    }
    if (name === 'city') {
      if (value && !validateCity(value)) {
        alert('City should only contain letters and spaces.');
        return;
      }
    }
    if (name === 'pincode') {
      if (value && !validatePincode(value)) {
        alert('Pincode must contain only digits (up to 6).');
        return;
      }
    }
    if (name === 'mobileNo') {
      if (value && !validateMobile(value)) {
        alert('Mobile Number must contain only digits (up to 10).');
        return;
      }
    }
    if (name === 'taxNo1' || name === 'taxNo2') {
      if (value && !validateTaxNo(value)) {
        alert('Tax Numbers should only contain letters and numbers.');
        return;
      }
    }


    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    if (!currentUser) {
      alert("You must be logged in to add a partner.");
      setSubmitting(false);
      return;
    }

    if (!formData.name) {
      alert("Name is required.");
      setSubmitting(false);
      return;
    }

    if (!formData.commissionAmount || !formData.commissionPercentage) {
      alert("Both Commission Amount and Percentage are required.");
      setSubmitting(false);
      return;
    }

    if (formData.aadharNo && formData.aadharNo.length !== 12) {
      alert("Aadhar Number must be exactly 12 digits.");
      setSubmitting(false);
      return;
    }

    if (formData.pincode && formData.pincode.length !== 6) {
      alert("Pincode must be exactly 6 digits.");
      setSubmitting(false);
      return;
    }

    if (formData.mobileNo && formData.mobileNo.length !== 10) {
      alert("Mobile Number must be exactly 10 digits.");
      setSubmitting(false);
      return;
    }



    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const partnersRef = ref(database, `users/${encodedEmail}/partners`);
      const partnerPayload = {
        ...formData,
        createdAt: partnerData ? partnerData.createdAt : serverTimestamp(),
        createdBy: currentUser.email
      };

      if (partnerData && partnerData.id) {
        const partnerRef = ref(database, `users/${encodedEmail}/partners/${partnerData.id}`);
        await update(partnerRef, partnerPayload);
        alert("Partner Data Updated Successfully");
      } else {
        await push(partnersRef, partnerPayload);
        alert("Partner Data Saved Successfully");
        setFormData({
          name: "",
          companyName: "",
          address: "",
          address2: "",
          landmark: "",
          state: "Maharashtra",
          city: "",
          pincode: "",
          country: "India",
          taxNo1: "",
          taxNo2: "",
          mobileNo: "",
          email: "",
          aadharNo: "",
          commissionAmount: "",
          commissionPercentage: ""
        });
      }

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error saving data:", error);
      alert("Error saving partner data: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: "",
      companyName: "",
      address: "",
      address2: "",
      landmark: "",
      state: "Maharashtra",
      city: "",
      pincode: "",
      country: "India",
      taxNo1: "",
      taxNo2: "",
      mobileNo: "",
      email: "",
      aadharNo: "",
      commissionAmount: "",
      commissionPercentage: ""
    });
    if (onCancel) onCancel();
  };

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '2rem auto',
      padding: '2rem',
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      minHeight: '100vh',
    },
    formWrapper: {
      backgroundColor: '#ffffff',
      borderRadius: '20px',
      padding: '2.5rem',
      border: '1px solid #e2e8f0',
    },
    heading: {
      fontSize: '2rem',
      fontWeight: '700',
      marginBottom: '2rem',
      color: '#1f2937',
      textAlign: 'center',
    },
    loadingContainer: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#6b7280',
    },
    loadingSpinner: {
      width: '3rem',
      height: '3rem',
      border: '4px solid #e5e7eb',
      borderTop: '4px solid #2563eb',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      margin: '0 auto 1rem',
    },
    formLayout: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '2rem',
    },
    twoColumnLayout: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '2rem',
    },
    section: {
      backgroundColor: '#fff',
      padding: '1.5rem',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    },
    sectionHeading: {
      fontSize: '1.25rem',
      fontWeight: '600',
      marginBottom: '1rem',
      color: '#1f2937',
    },
    inputGroup: {
      marginBottom: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    label: {
      display: 'block',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.25rem',
    },
    requiredAsterisk: {
      color: '#ef4444',
      marginLeft: '0.25rem',
    },
    input: {
      display: 'block',
      width: '95%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    emailInput: {
      display: 'block',
      width: '100%', // Increased width to match CustomerForm
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    select: {
      display: 'block',
      width: '95%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#fff',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
      backgroundPosition: 'right 0.5rem center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '1em 1em',
      paddingRight: '2rem',
    },
    buttonContainer: {
      marginTop: '2rem',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      paddingTop: '1rem',
      borderTop: '1px solid #e2e8f0',
    },
    button: {
      padding: '0.5rem 1.5rem',
      borderRadius: '6px',
      fontWeight: '500',
      fontSize: '0.875rem',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
    },
    submitButton: {
      backgroundColor: '#10b981',
      color: 'white',
    },
    cancelButton: {
      backgroundColor: '#ef4444',
      color: 'white',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.formWrapper}>
        <h1 style={styles.heading}>🤝 {partnerData ? 'Edit Partner' : 'Add New Partner'}</h1>

        {loading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner}></div>
            <p>Loading form data...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={styles.formLayout}>
              <div style={styles.twoColumnLayout}>
                <div style={styles.section}>
                  <h2 style={styles.sectionHeading}>📋 Partner Details</h2>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      Name<span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter partner name (letters and spaces only)"
                      required
                      title="Only letters and spaces are allowed"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Company Name</label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter company name (letters and spaces only)"
                      title="Only letters and spaces are allowed"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Aadhar No</label>
                    <input
                      type="text"
                      name="aadharNo"
                      value={formData.aadharNo}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter 12-digit Aadhar number"
                      maxLength={12}
                      title="Exactly 12 digits are required"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      Commission Amount<span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <input
                      type="number"
                      name="commissionAmount"
                      value={formData.commissionAmount}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter commission amount"
                      required
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      Commission Percentage<span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <input
                      type="number"
                      name="commissionPercentage"
                      value={formData.commissionPercentage}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter commission percentage"
                      required
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                <div style={styles.section}>
                  <h2 style={styles.sectionHeading}>📍 Address Information</h2>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Address</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter address"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Address 2</label>
                    <input
                      type="text"
                      name="address2"
                      value={formData.address2}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter address 2"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Landmark</label>
                    <input
                      type="text"
                      name="landmark"
                      value={formData.landmark}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter landmark"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter city (letters and spaces only)"
                      title="Only letters and spaces are allowed"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Pincode</label>
                    <input
                      type="text"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter 6-digit pincode"
                      maxLength={6}
                      title="Exactly 6 digits are required"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Country</label>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      style={styles.select}
                      disabled={loading || countries.length === 0}
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>State</label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      style={styles.select}
                      disabled={loading || !formData.country}
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state.name} value={state.name}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div style={styles.section}>
                <h2 style={styles.sectionHeading}>📞 Contact & Tax Information</h2>
                <div style={styles.twoColumnLayout}>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Mobile No</label>
                    <input
                      type="text"
                      name="mobileNo"
                      value={formData.mobileNo}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter 10-digit mobile number"
                      maxLength={10}
                      title="Exactly 10 digits are required"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>E-Mail ID</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      style={styles.emailInput} // Updated to use emailInput style
                      placeholder="Enter email (e.g., example@domain.com)"
                      title="Enter a valid email address (e.g., example@domain.com)"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Tax No 1</label>
                    <input
                      type="text"
                      name="taxNo1"
                      value={formData.taxNo1}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter tax number 1 (letters and numbers only)"
                      title="Only letters and numbers are allowed"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Tax No 2</label>
                    <input
                      type="text"
                      name="taxNo2"
                      value={formData.taxNo2}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter tax number 2 (letters and numbers only)"
                      title="Only letters and numbers are allowed"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.buttonContainer}>
              <button
                type="button"
                onClick={handleCancel}
                style={{ ...styles.button, ...styles.cancelButton }}
                disabled={submitting || loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{ ...styles.button, ...styles.submitButton }}
                disabled={submitting || loading}
              >
                {submitting ? '⏳ Saving...' : partnerData ? 'Update Partner' : 'Add Partner'}
              </button>
            </div>
          </form>
        )}
      </div>

      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        @media (max-width: 768px) {
          .twoColumnLayout {
            grid-template-columns: 1fr;
          }
          .buttonContainer {
            flex-direction: column;
          }
          .button {
            width: 100%;
            justify-content: center;
          }
        }
        input:focus,
        select:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
        }
        input:hover,
        select:hover {
          border-color: #a1a1aa;
        }
        button:hover:not(:disabled) {
          filter: brightness(90%);
        }
      `}</style>
    </div>
  );
};

export default PartnerForm;